import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Select, TextArea, Text } from "../UI";
import styled, { useTheme } from "styled-components";
import { observer, inject } from "mobx-react";
import Checkbox from "../UI/Checkbox/Checkbox";
import Modal from "../UI/Modal";
import userImg from "../asserts/img/fi-rs-user.svg";
import orderImg from "../asserts/img/taskType.svg";
import CallsImg from "../asserts/img/fi-rs-file-invitation.svg";
import TasksImg from "../asserts/img/task_list.svg";
import orderLineImg from "../asserts/img/order_list.svg";
import hoursImg from "../asserts/img/hours.svg";
import descriptionImg from "../asserts/img/q.svg";
import dateSelectImg from "../asserts/img/calender_input.svg";
import { inputsHasError, useInput } from "../utils/hooks";
import { isNotEmpty, minLength, withError } from "../validation";
import { DatePicker, LocalizationProvider } from "@material-ui/pickers";
import DateFnsUtils from "@material-ui/pickers/adapter/date-fns";
import { he } from "date-fns/locale";
import { ClipLoader } from "react-spinners";
import { subMonths } from "date-fns";
import { sortListFirstHeb } from "../utils/helper";
import { maxLength } from "../validation/minLength";
import { parseDateOnly } from "./Common";

const Error = styled("div")`
  color: red;
  text-align: "start";
`;

const StyledDiv = styled.div(({ error, theme, height }) => ({
  borderRadius: 10,
  height: height ? height : "41px",
  border:
    !!error === true
      ? `1px solid ${theme.color.error}`
      : `1px solid ${theme.color.darkGray}`,
  fontSize: 16,
  display: "flex",
  paddingInlineEnd: "1em",
  paddingInlineStart: "1em",
  lineHeight: 1,
  alignItems: "center",
}));

const StyledInput = styled.input(() => ({
  width: "100%",
  backgroundColor: "transparent",
  border: "none",
  "::placeholder": {
    color: "#BDBFCE",
  },
  lineHeight: 1,
}));

const NewReportDetail = ({
  reportDetailsStore,
  selectedReport,
  userStore,
  onClose,
}) => {
  const { t, i18n } = useTranslation("report_details");
  const isRtl = i18n.dir() === "rtl";
  const theme = useTheme();
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [calls, setCalls] = useState([]);
  const [report, setReport] = useState({
    CURDATE: selectedReport?.data?.CURDATE || new Date(),
    USERID: userStore.userDetails?.ZCR_USERID,
    CUSTNAME: selectedReport?.data?.CUSTNAME,
    HOURS: selectedReport?.data?.HOURS || 0,
    ORDNAME: selectedReport?.data?.ORDNAME,
    OLINE: selectedReport?.data?.OLINE,
    JOBDESC: selectedReport?.data?.JOBDESC,
    JOBDESCLONG: selectedReport?.data?.JOBDESCLONG,
    ZCOR_TASKNAME: selectedReport?.data?.TASKNAME,
    ZCOR_TASKDES: selectedReport?.data?.TASKDES,
    ZCOR_DOCNO: selectedReport?.data?.DOCNO,
    FREEOFCHARGE: selectedReport?.data?.FREEOFCHARGE,
  });

  const [customer, setCustomer] = useState(
    selectedReport?.data?.CUSTNAME
      ? {
          value: selectedReport?.data?.CUSTNAME,
          label: selectedReport?.data?.CUSTDES,
        }
      : null
  );

  const [order, setOrder] = useState(
    !!report?.ORDNAME
      ? {
          value: selectedReport?.data?.ORDNAME,
          label: selectedReport?.data?.DETAILS,
        }
      : null
  );

  const [task, setTask] = useState(
    selectedReport?.data?.ZCOR_TASKNAME
      ? {
          value: selectedReport?.data?.ZCOR_TASKNAME,
          label:
            selectedReport?.data?.ZCOR_TASKDES +
            " " +
            selectedReport?.data?.ZCOR_TASKNAME,
        }
      : null
  );

  const [call, setCall] = useState(
    selectedReport?.data?.ZCOR_DOCNO
      ? {
          value: selectedReport?.data?.ZCOR_DOCNO,
          label: selectedReport?.data?.ZCOR_DOCNO,
        }
      : null
  );

  const [orderslines, setOrderLines] = useState([]);
  const [orderline, setOrderLine] = useState(
    selectedReport?.data?.OLINE
      ? {
          value: selectedReport?.data?.OLINE,
          label: selectedReport?.data?.PARTDES,
        }
      : null
  );

  const [selectedDate, setSelectedDate] = useState(
    // Parse via parseDateOnly so the picker shows the calendar day encoded in
    // CURDATE, not the day shifted through the viewer's local timezone.
    parseDateOnly(selectedReport?.data?.CURDATE)
  );
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const isNone = (e) => {
    if (Object.values(placeholders).includes(e)) return false;
    return true;
  };

  const isNotEmptyWithError = withError(
    isNotEmpty,
    t("error:text_field_empty_error")
  );

  const minLengthWithError = withError(
    (input) => minLength(input, 5),
    t("error:text_field_min_length_error", { count: 5 })
  );

  const isNoneWithError = () =>
    withError(isNone, t("error:text_field_empty_error"));

  const placeholders = {
    call: t("calls_list"),
    hours: t("hours"),
    description: t("description"),
    task: t("tasks_list"),
    order: t("choose_order"),
    orderline: t("choose_orderline"),
    customer: t("choose_customer"),
    date: t("choose_date"),
  };

  const isNumericNonZero = withError(
    (input) => !isNaN(parseFloat(input)) && isFinite(input) && input > 0,
    t("error:text_field_numeric_non_zero_error")
  );

  const maxLengthWithError = withError(
    (input) => maxLength(input, 120),
    t("error:text_field_max_length_error", { count: 120 })
  );

  const descriptionInput = useInput({
    validation: [isNotEmptyWithError, minLengthWithError, maxLengthWithError],
    placeholder: t("description"),
    value: `${report?.JOBDESC ?? ""}${report?.JOBDESCLONG ?? ""}`,
  });

  const hoursInput = useInput({
    validation: [
      isNotEmptyWithError,
      isNoneWithError("hours"),
      isNumericNonZero,
    ],
    type: "number",
    placeholder: t("hours"),
    value: report?.HOURS || "",
  });

  const tasksInput = useInput({
    validation: [isNotEmptyWithError, isNoneWithError("task")],
    value: task || placeholders["task"],
  });

  const callsInput = useInput({
    validation: [isNotEmptyWithError, isNoneWithError("call")],
    value: call || placeholders["call"],
  });

  const customerInput = useInput({
    validation: [isNotEmptyWithError, isNoneWithError("customer")],
    value: customer || placeholders["customer"],
  });

  const orderInput = useInput({
    validation: [isNotEmptyWithError, isNoneWithError("order")],
    value: order || placeholders["order"],
  });

  const orderlineInput = useInput({
    validation: [isNotEmptyWithError, isNoneWithError("orderline")],
    value: orderline || placeholders["orderline"],
  });

  const dateInput = useInput({
    validation: [isNotEmptyWithError, isNoneWithError("date")],
    value: report?.CURDATE,
    startAdornment: (
      <img
        alt="date"
        src={dateSelectImg}
        style={{
          marginLeft: 5,
          marginRight: 5,
        }}
      />
    ),
  });

  const handleCustChange = (value) => {
    setCustomer(value);
    setReport({ ...report, CUSTNAME: value?.value });
  };

  const handleOrderChange = (value) => {
    setOrder(value);
    setReport({ ...report, ORDNAME: value?.value });
  };

  const handleOrderLineChange = (value) => {
    setOrderLine(value);
    setReport({ ...report, OLINE: parseInt(value?.value) });
  };

  const handleDescriptionChange = (e) => {
    setReport({
      ...report,
      JOBDESC: e.target.value.substring(0, 67),
      JOBDESCLONG: e.target.value.substring(67),
    });
  };

  const handleHoursChange = (e) => {
    setReport({ ...report, HOURS: e.target.value });
  };

  const handleTasksListChange = (value) => {
    setTask(value);

    const taskValue = value.value;
    if (!!taskValue?.OCUSTNAME) {
      setCustomer({ value: taskValue?.OCUSTNAME, label: taskValue.OCUSTDES });
    }
    if (!!taskValue?.ORDNAME) {
      setOrder({ value: taskValue.ORDNAME, label: taskValue.ORDPDES });
    }
    if (!!taskValue?.OLINE) {
      setOrderLine({ value: taskValue.OLINE, label: taskValue.PARTDES });
    }
    if (taskValue) {
      setReport({
        ...report,
        ZCOR_TASKNAME: value.value.TASKNAME,
        CUSTNAME: !!taskValue?.OCUSTNAME
          ? taskValue?.OCUSTNAME
          : report?.CUSTNAME,
        ORDNAME: !!taskValue?.ORDNAME ? taskValue?.ORDNAME : report?.ORDNAME,
        OLINE: !!taskValue?.OLINE ? taskValue?.OLINE : report?.OLINE,
      });
    }
  };

  const handleCallsListChange = (value) => {
    setCall(value);
    setReport({ ...report, ZCOR_DOCNO: value.value });
  };

  const handleDateChange = (date) => {
    var localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60 * 1000
    );

    setReport({ ...report, CURDATE: localDate });
  };

  const handleDateClick = (event) => {
    setOpen((open) => !open);
    setAnchorEl(event.currentTarget);
  };

  const handelNewReportDetail = async () => {
    if (isSaving) return;

    reportDetailsStore.setError("");
    if (
      !inputsHasError(
        descriptionInput,
        dateInput,
        customerInput,
        orderInput,
        orderlineInput,
        hoursInput
      )
    ) {
      const data = {
        ...report,
        HOURS: parseFloat(report.HOURS),
      };
      delete data.DETAILS;
      setSaving(true);

      selectedReport.data !== undefined
        ? await reportDetailsStore.updateReport({
            REPORT: selectedReport?.REPORT,
            LINE: selectedReport?.LINE,
            data: data,
            FREEOFCHARGE: selectedReport.FREEOFCHARGE === "Y" ? "N" : "Y",
          })
        : await reportDetailsStore.addReport(data);
      setSaving(false);

      if (reportDetailsStore.isError) {
        setError(reportDetailsStore.error);
      } else {
        onClose();
      }
    }
  };

  const handelNoChargeChange = (isChecked) => {
    setReport({
      ...report,
      FREEOFCHARGE: isChecked ? "N" : "Y",
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      reportDetailsStore.setError("");
      userStore.setError("");
      const customersArray = userStore.customers || [];
      const customers = customersArray
        .map((cust) => ({
          value: cust.CUSTNAME,
          label: cust.CUSTDES,
        }))
        .sort(sortListFirstHeb);

      if (!userStore.isError) {
        setCustomers(customers);
      }

      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      userStore.setError("");
      const ordersArray = await userStore.getCustomerOrders({
        CUSTNAME: customer?.value,
      });

      await reportDetailsStore.getTasksByCustomer({
        CUSTNAME: customer?.value,
        DATE: subMonths(new Date(), 1),
      });

      await reportDetailsStore.getCallsByCustomer({
        CUSTNAME: customer?.value,
        DATE: subMonths(new Date(), 1),
      });

      const tasksArray = reportDetailsStore.customerTasks || [];
      const callsArray = reportDetailsStore.customerCalls || [];

      const orders = ordersArray
        .map((ord) => ({
          value: ord.ORDNAME,
          label: ord.DETAILS,
          ORDERITEMS_SUBFORM: ord.ORDERITEMS_SUBFORM,
          //.filter(
          //   (line) => line.CLOSEDBOOL !== "Y"
          // ),
        }))
        .filter((ord) => ord.ORDERITEMS_SUBFORM.length > 0);

      const tasks = tasksArray.map((item) => ({
        value: item,
        label: item.TASKNAME + " " + item.TASKDES,
      }));

      const calls = callsArray.map((item) => ({
        value: item.DOCNO,
        label: item.DOCNO + " " + item.DETAILS,
      }));

      if (!reportDetailsStore.isError) {
        setTasks(tasks);
        setCalls(calls);
      }

      if (!userStore.isError) {
        setOrders(orders);
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    if (selectedReport?.data === undefined) {
      setOrderLine(null);
    }
    const selectedOrder = orders.find((ord) => ord.value === order?.value);
    const orderlinesArray = selectedOrder?.ORDERITEMS_SUBFORM || [];
    const orderlines = orderlinesArray.map((line) => ({
      value: line.LINE,
      label: line.PDES,
    }));
    setOrderLines(orderlines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orders]);

  return (
    <Modal
      onClose={() => onClose(false)}
      title={
        selectedReport?.data !== undefined
          ? t("update_report_title")
          : t("new_report_title")
      }
      width="665px">
      <div
        style={{
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}>
        {isLoading && (
          <div>
            <ClipLoader color={theme.color.label} />
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 14,
            marginBottom: "1rem",
          }}>
          <LocalizationProvider dateAdapter={DateFnsUtils} locale={he}>
            <DatePicker
              closeOnSelect={true}
              mask="__/__/__"
              inputFormat="dd/MM/yy"
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                handleDateChange(date);
                setOpen(false);
              }}
              onClose={() => setOpen(false)}
              open={open}
              PopperProps={{
                placement: "bottom-end",
                anchorEl: anchorEl,
              }}
              renderInput={({ inputRef, inputProps }) => (
                <div
                  style={{
                    width: "50%",
                  }}>
                  <StyledDiv>
                    <img
                      style={{
                        width: 22,
                        height: 22,
                        marginInlineEnd: "1em",
                        alignSelf: "center",
                      }}
                      src={dateSelectImg}
                      alt="input-start-img"
                    />
                    <StyledInput
                      style={{ textAlign: isRtl ? "end" : "start" }}
                      {...inputProps}
                      inputFormat="dd/MM/yy"
                      validation={[
                        isNotEmptyWithError,
                        isNoneWithError("date"),
                      ]}
                      ref={inputRef}
                      onChange={(e) => handleDateChange(e)}
                      onClick={handleDateClick}
                    />
                  </StyledDiv>
                </div>
              )}
            />
          </LocalizationProvider>
          <Select
            {...customerInput}
            style={{ textAlign: "start", width: "50%" }}
            startImg={userImg}
            options={customers || []}
            onChange={(value) => handleCustChange(value)}></Select>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 14,
            marginBottom: "1rem",
          }}>
          <Select
            {...orderInput}
            startImg={orderImg}
            style={{ textAlign: "start", width: "50%" }}
            options={orders || []}
            onChange={(value) => handleOrderChange(value)}></Select>
          <Select
            {...orderlineInput}
            startImg={orderLineImg}
            style={{ textAlign: "start", width: "50%" }}
            options={orderslines || []}
            onChange={(value) => handleOrderLineChange(value)}></Select>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 14,
            marginBottom: "1rem",
          }}>
          <Input
            style={{ textAlign: "start", width: "50%" }}
            {...hoursInput}
            startImg={hoursImg}
            onWheel={(e) => e.target.blur()}
            onBlur={(e) => handleHoursChange(e)}
          />
          <div style={{ width: "50%" }}></div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}>
          <Select
            style={{ textAlign: "start", width: "100%" }}
            {...tasksInput}
            creatable={true}
            startImg={TasksImg}
            options={tasks || []}
            onChange={(value) => handleTasksListChange(value)}></Select>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}>
          <Select
            {...callsInput}
            creatable={true}
            style={{ textAlign: "start", width: "100%" }}
            startImg={CallsImg}
            options={calls || []}
            onChange={(value) => handleCallsListChange(value)}></Select>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}>
          <TextArea
            startImg={descriptionImg}
            {...descriptionInput}
            name="description"
            placeholder={placeholders["description"]}
            rows={4}
            isRtl={isRtl}
            cols={90}
            onBlur={(e) => handleDescriptionChange(e)}
          />
        </div>
        {!!error && <Error> {t(error)} </Error>}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div
            style={{
              flexDirection: "column",
              display: "flex",
              flexBasis: "50%",
              gap: 5,
              marginTop: 20,
              paddingInlineStart: 5,
            }}>
            <Text
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#9FA2B4",
                textAlign: "start",
              }}>
              {t("NOCHARGEFLAG")}
            </Text>
            <Checkbox
              checked={report?.FREEOFCHARGE === "Y"}
              onChange={handelNoChargeChange}
              width={20}
              height={20}></Checkbox>
          </div>
          <Button
            onClick={handelNewReportDetail}
            buttonStyle="main"
            style={{ marginTop: 30 }}>
            {isSaving ? (
              <div style={{ paddingTop: 6 }}>
                <ClipLoader color={theme.color.white} size={22} />
              </div>
            ) : selectedReport?.data !== undefined ? (
              t("update_report")
            ) : (
              t("add_report")
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default inject(
  "reportDetailsStore",
  "userStore"
)(observer(NewReportDetail));
